/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  ICliente,
  IConfigVecino,
  IControl,
  ICrearControl,
  IFilter,
  IListado,
  IQueryParam,
  ISirena,
  ISocketMessage,
} from 'modelos/src';
import { firstValueFrom, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { ListadosService } from '../../../auxiliares/servicios/listados.service';
import { WebSocketService } from '../../../auxiliares/servicios/websocket';
import { ControlesService } from '../controles.service';

@Component({
  selector: 'app-crear-editar-controles',
  templateUrl: './crear-editar-controles.component.html',
  styleUrls: ['./crear-editar-controles.component.scss'],
  standalone: false,
})
export class CrearEditarControlesComponent implements OnInit, OnDestroy {
  data = inject<IControl>(MAT_DIALOG_DATA);
  private fb = inject(UntypedFormBuilder);
  private dialogRef =
    inject<MatDialogRef<CrearEditarControlesComponent>>(MatDialogRef);
  private service = inject(ControlesService);
  private helper = inject(HelperService);
  private listadosService = inject(ListadosService);
  private webSocketService = inject(WebSocketService);

  formulario?: UntypedFormGroup;
  loading = false;
  title?: string;
  hide = true;

  clientes: ICliente[] = [];

  configs: IConfigVecino[] = [];
  configs$?: Observable<IConfigVecino[]>;
  inputConfigs$ = new Subject<string>();

  sirenas: ISirena[] = [];
  sirenas$?: Observable<ISirena[]>;
  inputSirenas$ = new Subject<string>();

  private clientes$?: Subscription;
  private wsSubscription?: Subscription;

  private crearFormulario(): void {
    this.title = this.data?._id ? 'Editar Control' : 'Crear Control';
    this.formulario = this.fb.group({
      chipIdSirena: [],
      etiqueta: [this.data?.etiqueta],
      chipId: [
        this.data?.chipId,
        [Validators.required, Validators.minLength(4)],
      ],
      idCliente: [this.data?.idCliente],
      idConfigVecino: [this.data?.idConfigVecino],
      incrementar: [false],
    });

    if (this.data?.configVecino) {
      this.configs = [this.data.configVecino];
      this.configs$ = of([this.data.configVecino]);
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  private getData(): ICrearControl {
    const data: ICrearControl = {
      chipId: this.formulario?.get('chipId')?.value,
      idCliente: this.formulario?.get('idCliente')?.value,
      idConfigVecino: this.formulario?.get('idConfigVecino')?.value,
      etiqueta: this.formulario?.get('etiqueta')?.value?.trim() || undefined,
    };
    return data;
  }

  public async enviar(): Promise<void> {
    this.loading = true;
    try {
      const data = this.getData();
      console.log('data', data);
      if (this.data) {
        await firstValueFrom(this.service.editar(this.data._id!, data));
        this.helper.notifSuccess('Editado correctamente');
        this.dialogRef.close(true);
      } else {
        await firstValueFrom(this.service.crear(data));
        this.helper.notifSuccess('Creado correctamente');
        //
        this.formulario?.patchValue({ chipId: null });
        this.formulario?.patchValue({ idConfigVecino: null });
        // Incremento de etiqueta para cargar controles en serie
        const incrementar = this.formulario?.get('incrementar')?.value;
        const siguiente = HelperService.siguienteEtiqueta(data.etiqueta);
        if (incrementar && siguiente) {
          this.formulario?.patchValue({ etiqueta: siguiente });
        } else {
          if (incrementar && data.etiqueta) {
            this.helper.notifWarn(
              `La etiqueta "${data.etiqueta}" no termina en número, no se pudo incrementar`,
            );
          }
          this.formulario?.patchValue({ etiqueta: null });
        }
        //
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  // Listados

  private async listarClientes(): Promise<void> {
    const query: IQueryParam = {
      sort: 'nombre',
      select: 'nombre',
    };

    this.clientes$?.unsubscribe();
    this.clientes$ = this.listadosService
      .subscribe<IListado<ICliente>>('clientes', query)
      .subscribe((data) => {
        this.clientes = data.datos;
        console.debug(new Date().toLocaleString(), `listado de clientes`, data);
      });
    await this.listadosService.getLastValue('clientes', query);
  }

  private crearFiltroConfig(search: string): IQueryParam {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filtro: IFilter<any> = {};
    if (search) {
      filtro.$or = [
        { 'datosPersonales.dni': { $regex: search, $options: 'i' } },
        { 'datosPersonales.nombre': { $regex: search, $options: 'i' } },
      ];
    }
    const idCliente = this.formulario?.get('idCliente')?.value;
    if (idCliente) {
      filtro.idCliente = idCliente;
    }

    const populate = {
      path: 'cliente',
      select: 'nombre',
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filtro),
      limit: 10,
      populate: JSON.stringify(populate),
      select: 'datosPersonales idCliente cliente',
    };
    return query;
  }

  private crearFiltroSirena(search: string): IQueryParam {
    const filtro: IFilter<ISirena> = {};
    if (search) {
      filtro.$or = [
        { chipId: { $regex: search, $options: 'i' } },
        { direccionGps: { $regex: search, $options: 'i' } },
        { direccionManual: { $regex: search, $options: 'i' } },
      ];
    }
    const query: IQueryParam = {
      filter: JSON.stringify(filtro),
      limit: 10,
      select: 'chipId direccionGps direccionManual',
    };
    return query;
  }

  onSearchConfigs() {
    this.configs$ = of([]);
    this.inputConfigs$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((term) =>
          this.listadosService.listarConfigVecinos$(
            this.crearFiltroConfig(term),
          ),
        ),
      )
      .subscribe((data) => {
        console.log('configs encontradas', data);
        this.configs = data;
        this.configs$ = of(data);
      });
  }

  public searchSirenas = (term: string, item: ISirena) => {
    if (item.chipId?.toLowerCase().includes(term.toLowerCase())) return true;
    if (item.direccionGps?.toLowerCase().includes(term.toLowerCase()))
      return true;
    if (item.direccionManual?.toLowerCase().includes(term.toLowerCase()))
      return true;
    return false;
  };

  // Listados
  private async listarSirenas() {
    const filter: IFilter<ISirena> = {
      'configs.puedeEnviarControlesDesconocidos': true,
    } as any;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      select: 'chipId direccionGps direccionManual',
    };

    this.sirenas = await firstValueFrom(
      this.listadosService.listarSirenas$(query),
    );

    console.log('Sirenas para controles desconocidos:', this.sirenas);
  }

  // ################################################################################
  // ################################# SUSCRIBE #####################################
  // ################################################################################

  private suscribeWsUpdates() {
    this.wsSubscription = this.webSocketService.getMessage().subscribe({
      next: this.handleUpdateResponse.bind(this),
    });
  }

  private handleUpdateResponse(message: ISocketMessage) {
    if (!this.data) {
      if (message.paths?.includes('controlnoconocido')) {
        const body: {
          chipIdControl: string;
          chipIdSirena: string;
          etiqueta: string;
        } =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          message.body as any;
        const sirenaElegida = this.formulario?.get('chipIdSirena')?.value;
        const etiqueta = body.etiqueta;
        const chipId = body.chipIdControl;
        if (sirenaElegida === body.chipIdSirena) {
          if (etiqueta) {
            this.helper.notifWarn(
              `Control chipId ${chipId} cargado con la etiqueta ${etiqueta}`,
            );
          } else {
            this.formulario?.patchValue({ chipId });
          }
        }
      }
    }
  }

  //

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.crearFormulario();
    this.suscribeWsUpdates();
    await Promise.all([this.listarClientes(), this.listarSirenas()]);
    this.onSearchConfigs();
    console.log('this.data', this.data);

    this.loading = false;
  }

  ngOnDestroy(): void {
    this.clientes$?.unsubscribe();
    this.wsSubscription?.unsubscribe();
  }
}
